import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import DisclaimerBanner from './components/disclaimers/DisclaimerBanner';

// Auth Provider
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { BabyProfileProvider } from './contexts/BabyProfileContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Stripe Wrapper
import StripeWrapper from './components/StripeWrapper';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import FaqPage from './pages/public/FaqPage';
import PricingPage from './pages/public/PricingPage';
import ContactPage from './pages/public/ContactPage';
import LoginPage from './pages/public/LoginPage';
import SignupPaymentPage from './pages/public/SignupPaymentPage';
import FreeSignupPage from './pages/public/FreeSignupPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import AcknowledgementPage from './pages/public/AcknowledgementPage';
import StorePage from './pages/public/StorePage';
import TermsAndConditionsPage from './pages/public/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import GiftPage from './pages/public/GiftPage';

// Landing Pages with New URLs
import SmartSleepPlan from './pages/public/SmartSleepPlan';
import ExpertSleepPlan from './pages/public/ExpertSleepPlan';
import SleepPeace from './pages/public/SleepPeace';
import QuickSleepFix from './pages/public/QuickSleepFix';
import BetterSleep from './pages/public/BetterSleep';
import GiftOfSleep from './pages/public/GiftOfSleep';
import PreciousSleepGift from './pages/public/PreciousSleepGift';
import PracticalSleepGift from './pages/public/PracticalSleepGift';

// Original Landing Pages (for backward compatibility)
import LandingPageAI from './pages/public/LandingPageAI';
import LandingPageEvidence from './pages/public/LandingPageEvidence';
import LandingPageEmotional from './pages/public/LandingPageEmotional';
import LandingPageQuick from './pages/public/LandingPageQuick';
import LandingPageBold from './pages/public/LandingPageBold';
import GiftLandingPage from './pages/public/GiftLandingPage';
import GiftLandingPageEmotional from './pages/public/GiftLandingPage-Emotional';
import GiftLandingPagePractical from './pages/public/GiftLandingPage-Practical';

// User Pages
import Dashboard from './pages/protected/Dashboard';
import BabyProfileView from './pages/protected/BabyProfileView';
// Using SleepPlanViewer for the sleep plan route
import SleepPlan from './pages/protected/SleepPlanViewer';
import AddChild from './pages/protected/AddChild';
import EditChild from './pages/protected/EditChild';
import ResourcesPage from './pages/protected/Resources';
import SupportPage from './pages/protected/Support';

// Google Analytics page tracking component
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Send pageview with the current location pathname
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
  
  return null;
};

// Wrapper component to add the disclaimer banner
const AppContent = () => {
  const { currentUser, loading } = useAuth();
  
  // Check for authentication persistence on page load
  React.useEffect(() => {
    const checkAuthStatus = () => {
      // Check if we have authentication data in sessionStorage
      const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
      import('./utils/logger').then(({ logger }) => {
        logger.debug('App: Authentication status check', { 
          isAuthenticated, 
          hasCurrentUser: !!currentUser,
          loading,
          sessionStorageKeys: Object.keys(sessionStorage)
        });
      });
    };
    
    checkAuthStatus();
    
    // Add event listener for beforeunload to help debug refresh issues
    window.addEventListener('beforeunload', () => {
      import('./utils/logger').then(({ logger }) => {
        logger.debug('App: Page is about to unload, current auth state:', {
          isAuthenticated: sessionStorage.getItem('isAuthenticated') === 'true',
          hasCurrentUser: !!currentUser
        });
      });
    });
    
    return () => {
      window.removeEventListener('beforeunload', checkAuthStatus);
    };
  }, [currentUser, loading]);
  
  // Force scroll to top on route changes
  const handleRouteChange = () => {
    import('./utils/logger').then(({ logger }) => {
      logger.debug('Route changed, forcing scroll to top');
    });
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  return (
    <Router>
      <RouteTracker />
      <ScrollToTop />
      <Header />
      <main>
        {currentUser && <DisclaimerBanner userEmail={currentUser.email} />}
        <Routes onChange={handleRouteChange}>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* New Landing Page URLs */}
          <Route path="/smart-sleep-plan" element={<SmartSleepPlan />} />
          <Route path="/expert-sleep-plan" element={<ExpertSleepPlan />} />
          <Route path="/sleep-peace" element={<SleepPeace />} />
          <Route path="/quick-sleep-fix" element={<QuickSleepFix />} />
          <Route path="/better-sleep" element={<BetterSleep />} />
          <Route path="/gift-of-sleep" element={<GiftOfSleep />} />
          <Route path="/precious-sleep-gift" element={<PreciousSleepGift />} />
          <Route path="/practical-sleep-gift" element={<PracticalSleepGift />} />
          
          {/* Keep old routes temporarily for backward compatibility */}
          <Route path="/landing-ai" element={<LandingPageAI />} />
          <Route path="/landing-evidence" element={<LandingPageEvidence />} />
          <Route path="/landing-emotional" element={<LandingPageEmotional />} />
          <Route path="/landing-quick" element={<LandingPageQuick />} />
          <Route path="/landing-bold" element={<LandingPageBold />} />
          <Route path="/landing-gift" element={<GiftLandingPage />} />
          <Route path="/landing-gift-emotional" element={<GiftLandingPageEmotional />} />
          <Route path="/landing-gift-practical" element={<GiftLandingPagePractical />} />
          
          {/* New signup route */}
          <Route path="/signup" element={
            <StripeWrapper>
              <SignupPaymentPage />
            </StripeWrapper>
          } />
          {/* Free signup route - no payment required */}
          <Route path="/free-signup" element={<FreeSignupPage />} />
          {/* Keep old route temporarily for backward compatibility */}
          <Route path="/signup-payment" element={
            <StripeWrapper>
              <SignupPaymentPage />
            </StripeWrapper>
          } />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/acknowledgement" element={<AcknowledgementPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/gift" element={
            <StripeWrapper>
              <GiftPage />
            </StripeWrapper>
          } />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/child/:profileId" 
            element={
              <ProtectedRoute>
                <BabyProfileView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sleep-plan/:childId" 
            element={
              <ProtectedRoute>
                <SleepPlan />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/view-sleep-plan/:profileId" 
            element={
              <ProtectedRoute>
                <SleepPlan />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/add-child" 
            element={
              <ProtectedRoute>
                <AddChild />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/edit-child/:profileId" 
            element={
              <ProtectedRoute>
                <EditChild />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/resources" 
            element={
              <ProtectedRoute>
                <ResourcesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/support" 
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      <Footer />
    </Router>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <UserProvider>
          <BabyProfileProvider>
            <AppContent />
          </BabyProfileProvider>
        </UserProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
